<template>
  <div class="world-editor-items-list">
    <!-- Search, filter and refresh are one strip. Three stacked bars of chrome
         left less room for the items than for the controls above them. -->
    <div class="items-list-header">
      <h3 class="gm-section-title">Items</h3>
      <input
          v-model="searchQuery"
          class="gm-input search-input"
          placeholder="Search items"
          type="text"
          aria-label="Search items"
      />
      <select v-model="selectedType" class="gm-select type-filter" aria-label="Filter by type">
        <option value="">All types</option>
        <option v-for="(value, key) in itemTypes" :key="key" :value="value">
          {{ formatItemType(key) }}
        </option>
      </select>
      <button class="gm-btn gm-btn--sm gm-btn--ghost refresh-btn" title="Reload the item list" @click="refreshItems">
        <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><path d="M13 8a5 5 0 11-1.6-3.7M13 2.6V5h-2.4"/></svg>
      </button>
    </div>

    <div ref="itemsContainer" class="items-container gm-scroll">
      <div v-if="isLoading" class="gm-empty__hint loading-indicator">
        Loading items...
      </div>

      <div v-else-if="filteredItems.length === 0" class="gm-empty__hint no-items">
        No items found.
      </div>

      <div
          v-for="item in filteredItems"
          v-else
          :key="item.id"
          class="item-cell"
          draggable="true"
          @click="selectItem(item)"
          @dragstart="onDragStart($event, item)"
      >
        <div class="item-icon">
          <img
              v-if="item.icon"
              :alt="item.name"
              :src="item.icon"
              class="item-image"
          />
          <div v-else class="item-placeholder">
            {{ getItemInitials(item.name) }}
          </div>
        </div>
        <div class="item-info">
          <div class="item-name">{{ item.name }}</div>
          <div v-if="item.type" class="item-type">{{ item.type }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {itemsService} from '@/services/ItemsService';
import {dragDropService} from '@/services/DragDropService.js';
import {ItemTypeEnum} from '@/api/dx-backend/api.ts';

export default {
  name: 'WorldEditorItemsList',
  data() {
    return {
      items: [],
      isLoading: true,
      searchQuery: '',
      selectedType: '',
      error: null,
      itemTypes: ItemTypeEnum
    };
  },
  computed: {
    filteredItems() {
      let result = this.items;

      // Filter by type if selected
      if (this.selectedType) {
        result = result.filter(item => item.type === this.selectedType);
      }

      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        result = result.filter(item =>
            item.name.toLowerCase().includes(query) ||
            (item.description && item.description.toLowerCase().includes(query))
        );
      }

      return result;
    }
  },
  async mounted() {
    try {
      // Register event listeners
      itemsService.on('loadingStarted', this.onLoadingStarted);
      itemsService.on('itemsLoaded', this.onItemsLoaded);
      itemsService.on('loadingFailed', this.onLoadingFailed);

      // Initialize the service and load items
      await itemsService.initialize();
    } catch (error) {
      console.error('Error initializing items list:', error);
      this.error = error.message || 'Failed to load items';
      this.isLoading = false;
    }
  },
  beforeUnmount() {
    // Clean up event listeners
    itemsService.off('loadingStarted', this.onLoadingStarted);
    itemsService.off('itemsLoaded', this.onItemsLoaded);
    itemsService.off('loadingFailed', this.onLoadingFailed);
  },
  methods: {
    // Event handlers
    onLoadingStarted() {
      this.isLoading = true;
      this.error = null;
    },

    onItemsLoaded(items) {
      this.items = items;
      this.isLoading = false;
    },

    onLoadingFailed(error) {
      console.error('Failed to load items:', error);
      this.error = error.message || 'Failed to load items';
      this.isLoading = false;
    },

    // UI actions
    async refreshItems() {
      try {
        this.isLoading = true;
        await itemsService.refresh();
      } catch (error) {
        console.error('Error refreshing items:', error);
      }
    },

    selectItem(item) {
      // Emit the selected item to parent components
      this.$emit('item-selected', item);
    },

    // Helper methods
    getItemInitials(name) {
      if (!name) return '??';

      // Get first two characters of the name
      return name.substring(0, 2).toUpperCase();
    },

    formatItemType(type) {
      // Convert camelCase or snake_case to Title Case with spaces
      return type
          // Insert a space before all uppercase letters
          .replace(/([A-Z])/g, ' $1')
          // Replace underscores with spaces
          .replace(/_/g, ' ')
          // Capitalize first letter
          .replace(/^./, str => str.toUpperCase())
          // Trim any extra spaces
          .trim();
    },

    // Handle drag start event
    onDragStart(event, item) {
      console.log('Drag start:', item);

      // Set the drag data to the item's JSON string
      const itemJson = JSON.stringify(item);
      event.dataTransfer.setData('text/plain', itemJson);
      // Also set it as application/json for better compatibility
      event.dataTransfer.setData('application/json', itemJson);
      console.log('Set drag data:', itemJson);

      // Set the drag effect to 'copy' to indicate that we're copying the item
      event.dataTransfer.effectAllowed = 'copy';

      // Add a class to the dragged element for visual feedback
      event.target.classList.add('dragging');

      // Create a custom drag image
      const dragImage = this.createDragImage(item);
      if (dragImage) {
        // Use the custom drag image
        event.dataTransfer.setDragImage(dragImage, dragImage.width / 2, dragImage.height / 2);

        // Clean up the drag image after a short delay
        setTimeout(() => {
          if (dragImage.parentNode) {
            document.body.removeChild(dragImage);
          }
        }, 100);
      }

      // Notify the drag drop service that we've started dragging
      dragDropService.startDrag(item);

      // Remove the class when the drag ends
      const onDragEnd = () => {
        event.target.classList.remove('dragging');
        event.target.removeEventListener('dragend', onDragEnd);

        // We don't need to call endDrag here, as it will be handled by the DragDropService
        // dragDropService.endDrag();
      };

      event.target.addEventListener('dragend', onDragEnd);
    },

    // Create a custom drag image
    createDragImage(item) {
      // Create a new element for the drag image
      const dragImage = document.createElement('div');
      dragImage.className = 'custom-drag-image';

      // Add item icon
      const iconContainer = document.createElement('div');
      iconContainer.className = 'drag-image-icon';

      if (item.icon) {
        const img = document.createElement('img');
        img.src = item.icon;
        img.alt = item.name;
        iconContainer.appendChild(img);
      } else {
        iconContainer.textContent = this.getItemInitials(item.name);
      }

      // Add item name
      const nameContainer = document.createElement('div');
      nameContainer.className = 'drag-image-name';
      nameContainer.textContent = item.name;

      // Add item type if available
      if (item.type) {
        const typeContainer = document.createElement('div');
        typeContainer.className = 'drag-image-type';
        typeContainer.textContent = item.type;
        dragImage.appendChild(typeContainer);
      }

      // Assemble the drag image
      dragImage.appendChild(iconContainer);
      dragImage.appendChild(nameContainer);

      // Add styles
      Object.assign(dragImage.style, {
        position: 'absolute',
        top: '-1000px', // Position off-screen
        padding: '10px',
        background: '#444',
        border: '2px solid #1E90FF',
        borderRadius: '6px',
        boxShadow: '0 0 15px rgba(30, 144, 255, 0.8)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '5px',
        zIndex: '9999',
        pointerEvents: 'none'
      });

      Object.assign(iconContainer.style, {
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#555',
        borderRadius: '4px',
        marginBottom: '5px'
      });

      if (iconContainer.querySelector('img')) {
        Object.assign(iconContainer.querySelector('img').style, {
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain'
        });
      }

      Object.assign(nameContainer.style, {
        fontWeight: 'bold',
        fontSize: '12px',
        textAlign: 'center',
        maxWidth: '100px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      });

      if (dragImage.querySelector('.drag-image-type')) {
        Object.assign(dragImage.querySelector('.drag-image-type').style, {
          fontSize: '10px',
          color: '#aaa',
          textTransform: 'capitalize'
        });
      }

      // Add to the document
      document.body.appendChild(dragImage);

      return dragImage;
    }
  }
};
</script>

<style scoped>
.world-editor-items-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--gm-surface-1);
  color: var(--gm-text);
  overflow: hidden;
}

.items-list-header {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
  padding: var(--gm-gap-xs) var(--gm-gap-sm);
  background: var(--gm-surface-2);
  border-bottom: 1px solid var(--gm-border-dim);
}

.search-input,
.type-filter {
  min-width: 0;
  padding: 0.3rem 0.45rem;
  font-size: var(--gm-text-xs);
}

.search-input {
  flex: 1 1 55%;
}

.type-filter {
  flex: 1 1 45%;
}

.refresh-btn {
  padding: 0;
  width: 24px;
  height: 24px;
  flex: none;
}

.items-container {
  flex: 1;
  overflow-y: auto;
  padding: var(--gm-gap-sm);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(86px, 1fr));
  gap: var(--gm-gap-xs);
  align-content: start;
}

.loading-indicator, .no-items {
  grid-column: 1 / -1;
  padding: var(--gm-gap-lg);
  text-align: center;
  font-size: var(--gm-text-xs);
  color: var(--gm-text-dim);
}

.item-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  padding: var(--gm-gap-sm) var(--gm-gap-xs);
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: border-color var(--gm-duration) var(--gm-ease);
}

.item-cell:hover {
  border-color: var(--gm-border);
}

.item-cell.dragging {
  opacity: 0.9;
  border: 1px solid var(--gm-border-hi);
  z-index: 1000;
  position: relative;
  cursor: grabbing;
}

.item-icon {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--gm-gap-xs);
}

.item-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.item-placeholder {
  width: 100%;
  height: 100%;
  background: var(--gm-surface-1);
  color: var(--gm-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--gm-text-sm);
  border-radius: var(--gm-radius-sm);
}

.item-info {
  width: 100%;
  text-align: center;
}

.item-name {
  font-size: var(--gm-text-xs);
  line-height: var(--gm-leading-tight);
  color: var(--gm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-type {
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  letter-spacing: var(--gm-track-tight);
  color: var(--gm-text-dim);
  text-transform: uppercase;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .items-container {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}

@media (max-width: 768px) {
  .items-container {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  }

  .items-list-header {
    flex-wrap: wrap;
  }
}

.glyph {
  width: 12px;
  height: 12px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
</style>
